/**
 * FileMetadataApi - API client for the file metadata service
 * 
 * Handles authenticated requests to the DynamoDB-backed file metadata API.
 * This replaces direct S3 operations with O(1) metadata operations.
 * 
 * @module fileMetadataApi
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import awsExports from '../aws-exports';

// API base URL - deployed FileMetadataStack endpoint
const FILE_METADATA_API_URL = 'https://hptr1ug138.execute-api.us-east-1.amazonaws.com/prod';

const getApiBaseUrl = () => {
  // Check for FileMetadata API endpoint in aws-exports (allows override)
  const endpoints = awsExports.API?.endpoints || [];
  const fileMetadataEndpoint = endpoints.find(e => e.name === 'FileMetadataAPI');
  
  if (fileMetadataEndpoint) {
    return fileMetadataEndpoint.endpoint;
  }
  
  // Use deployed API URL
  return FILE_METADATA_API_URL;
};

/**
 * Get the current user's auth token
 * @returns {Promise<string>} The ID token
 * @throws {Error} If not authenticated
 */
const getAuthToken = async () => {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  return token;
};

/**
 * Make an authenticated API request
 * @param {string} path - API path (e.g., '/items')
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data
 */
const apiRequest = async (path, options = {}) => {
  const token = await getAuthToken();
  const baseUrl = getApiBaseUrl();
  
  const response = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers
    }
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    const error = new Error(data.message || `API error: ${response.status}`);
    error.code = data.error || 'API_ERROR';
    error.status = response.status;
    throw error;
  }
  
  return data;
};

/**
 * List items in a folder
 * @param {string} parentFolderId - Parent folder ID (or 'ROOT' for root)
 * @param {string} [nextToken] - Pagination token
 * @returns {Promise<{items: Array, nextToken?: string}>}
 */
export const listItems = async (parentFolderId = 'ROOT', nextToken) => {
  const params = new URLSearchParams({ parentFolderId });
  if (nextToken) params.append('nextToken', nextToken);
  
  return apiRequest(`/items?${params.toString()}`);
};

/**
 * Create a new file or folder
 * @param {Object} item - Item to create
 * @param {string} item.name - Item name
 * @param {string} item.type - 'file' or 'folder'
 * @param {string} [item.parentFolderId] - Parent folder ID (defaults to ROOT)
 * @returns {Promise<Object>} Created item
 */
export const createItem = async (item) => {
  return apiRequest('/items', {
    method: 'POST',
    body: JSON.stringify(item)
  });
};

/**
 * Update an item (rename or move)
 * @param {string} id - Item ID
 * @param {Object} updates - Fields to update
 * @param {string} [updates.name] - New name (for rename)
 * @param {string} [updates.parentFolderId] - New parent folder ID (for move)
 * @param {string} [updates.status] - New status
 * @returns {Promise<Object>} Updated item
 */
export const updateItem = async (id, updates) => {
  return apiRequest(`/items/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  });
};

/**
 * Delete an item
 * @param {string} id - Item ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteItem = async (id) => {
  return apiRequest(`/items/${id}`, {
    method: 'DELETE'
  });
};

/**
 * Get a presigned URL for uploading a file
 * @param {Object} params - Upload parameters
 * @param {string} params.fileName - File name
 * @param {string} [params.mimeType] - MIME type
 * @param {number} [params.size] - File size in bytes
 * @param {string} [params.parentFolderId] - Parent folder ID
 * @returns {Promise<{uploadUrl: string, itemId: string, s3Key: string, expiresIn: number}>}
 */
export const getUploadUrl = async ({ fileName, mimeType, size, parentFolderId }) => {
  return apiRequest(`/items/upload-url`, {
    method: 'POST',
    body: JSON.stringify({ fileName, mimeType, size, parentFolderId })
  });
};

/**
 * Get a presigned URL for downloading a file
 * @param {string} id - Item ID
 * @returns {Promise<{downloadUrl: string, fileName: string, expiresIn: number}>}
 */
export const getDownloadUrl = async (id) => {
  return apiRequest(`/items/${id}/download-url`);
};

/**
 * Bulk move items to a new folder
 * @param {string[]} itemIds - Array of item IDs to move
 * @param {string} destinationFolderId - Destination folder ID
 * @returns {Promise<{succeeded: string[], failed: Array<{id: string, error: string}>}>}
 */
export const bulkMove = async (itemIds, destinationFolderId) => {
  return apiRequest('/items/bulk-move', {
    method: 'POST',
    body: JSON.stringify({ itemIds, destinationFolderId })
  });
};

/**
 * Bulk delete items
 * @param {string[]} itemIds - Array of item IDs to delete
 * @returns {Promise<{succeeded: string[], failed: Array<{id: string, error: string}>}>}
 */
export const bulkDelete = async (itemIds) => {
  return apiRequest('/items/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ itemIds })
  });
};

/**
 * List all folders in the system (for folder picker)
 * Returns all folders with their full paths in a single API call.
 * @returns {Promise<{folders: Array<{id: string, name: string, path: string, parentFolderId: string}>, count: number}>}
 */
export const listAllFolders = async () => {
  return apiRequest('/items/folders');
};

/**
 * Search items by name (case-insensitive contains)
 * @param {string} query - Search query
 * @param {Object} [options] - Search options
 * @param {string} [options.rootFolderId] - Root folder to search within
 * @param {number} [options.limit] - Max results (default 100, max 500)
 * @returns {Promise<{items: Array, count: number, query: string}>}
 */
export const searchItems = async (query, options = {}) => {
  const params = new URLSearchParams({ q: query });
  if (options.rootFolderId) params.append('rootFolderId', options.rootFolderId);
  if (options.limit) params.append('limit', options.limit.toString());
  
  return apiRequest(`/items/search?${params.toString()}`);
};

/**
 * Confirm upload completion (update status to COMPLETE)
 * @param {string} id - Item ID
 * @returns {Promise<Object>} Updated item
 */
export const confirmUpload = async (id) => {
  return updateItem(id, { status: 'COMPLETE' });
};

// ============================================================================
// Multipart Upload API Methods
// ============================================================================

/**
 * Initiate a multipart upload for large files
 * @param {Object} params - Upload parameters
 * @param {string} params.fileName - File name
 * @param {string} [params.mimeType] - MIME type
 * @param {number} params.size - File size in bytes
 * @param {string} [params.parentFolderId] - Parent folder ID
 * @returns {Promise<{itemId: string, uploadId: string, s3Key: string}>}
 */
export const createMultipartUpload = async ({ fileName, mimeType, size, parentFolderId }) => {
  return apiRequest('/items/multipart', {
    method: 'POST',
    body: JSON.stringify({ fileName, mimeType, size, parentFolderId })
  });
};

/**
 * Get a presigned URL for uploading a specific part
 * @param {string} itemId - Item ID
 * @param {string} uploadId - S3 multipart upload ID
 * @param {number} partNumber - Part number (1-10000)
 * @returns {Promise<{uploadUrl: string, partNumber: number, expiresIn: number}>}
 */
export const getPartUploadUrl = async (itemId, uploadId, partNumber) => {
  return apiRequest(`/items/${itemId}/part-url`, {
    method: 'POST',
    body: JSON.stringify({ uploadId, partNumber })
  });
};

/**
 * Complete a multipart upload by assembling all parts
 * @param {string} itemId - Item ID
 * @param {string} uploadId - S3 multipart upload ID
 * @param {Array<{partNumber: number, etag: string}>} parts - Array of uploaded parts with ETags
 * @returns {Promise<{item: Object}>}
 */
export const completeMultipartUpload = async (itemId, uploadId, parts) => {
  return apiRequest(`/items/${itemId}/complete`, {
    method: 'POST',
    body: JSON.stringify({ uploadId, parts })
  });
};

/**
 * Abort a multipart upload and clean up S3 parts
 * @param {string} itemId - Item ID
 * @param {string} uploadId - S3 multipart upload ID
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const abortMultipartUpload = async (itemId, uploadId) => {
  return apiRequest(`/items/${itemId}/abort`, {
    method: 'POST',
    body: JSON.stringify({ uploadId })
  });
};

const fileMetadataApi = {
  listItems,
  createItem,
  updateItem,
  deleteItem,
  getUploadUrl,
  getDownloadUrl,
  bulkMove,
  bulkDelete,
  listAllFolders,
  searchItems,
  confirmUpload,
  // Multipart upload methods
  createMultipartUpload,
  getPartUploadUrl,
  completeMultipartUpload,
  abortMultipartUpload
};

export default fileMetadataApi;
